import Markdown from 'components/commercetools-ui/organisms/markdown';
import { isStringHtml } from 'helpers/utils/isStringHtml';
import type { JSX } from 'react';

export interface Feature {
  icon: string;
  title: string;
  description: string;
  ctaUrl?: string;
  ctaLabel?: string;
  ctaDataAnalytics?: string;
}

export interface FeatureSectionDynamicListProps {
  title: string;
  titleHtag?: string;
  bgColor: string;
  features: Feature[];
  sectionId?: string;
}

const FeatureSectionDynamicList: React.FC<FeatureSectionDynamicListProps> = ({
  title,
  titleHtag,
  bgColor,
  features,
  sectionId,
}) => {
  const classNameMain = `bg-${bgColor} pb-46 pt-34 lg:pt-64`;
  const TitleHtag = titleHtag ? (titleHtag as keyof JSX.IntrinsicElements) : 'h2';

  return (
    <div className={classNameMain} id={sectionId} data-id="feature-section-dynamic-list">
      <div className="mx-auto max-w-7xl px-24 lg:px-32" data-id="feature-section-container">
        <div className="mx-auto max-w-2xl lg:max-w-none" data-id="feature-section-header">
          <TitleHtag className="text-2xl font-medium" data-id="feature-section-title">
            {title}
          </TitleHtag>
        </div>
        <div className="mx-auto mt-34 max-w-2xl lg:mt-62 lg:max-w-none" data-id="feature-section-content">
          <dl
            className="grid max-w-xl grid-cols-1 gap-x-32 gap-y-22 lg:max-w-none lg:grid-cols-3 lg:gap-y-52"
            data-id="feature-section-list"
          >
            {features.map((feature, index) => (
              <div key={index} className="flex flex-col" data-id={`feature-item-${index}`}>
                <dt className="flex items-center gap-x-10 font-semibold" data-id={`feature-item-title-${index}`}>
                  <span className="material-symbols-outlined" aria-hidden="true" data-id={`feature-item-icon-${index}`}>
                    {feature.icon}
                  </span>
                  {feature.title}
                </dt>
                <dd
                  className="mt-0 flex flex-auto flex-col text-base leading-7 text-padi-gray-darker"
                  data-id={`feature-item-description-${index}`}
                >
                  <div className="prose max-w-none" data-id={`feature-item-content-${index}`}>
                    {isStringHtml(feature.description) ? (
                      <div
                        dangerouslySetInnerHTML={{ __html: feature.description }}
                        data-id={`feature-item-html-${index}`}
                      />
                    ) : (
                      <Markdown className="flex-auto" markdown={feature.description} />
                    )}
                  </div>
                  {feature.ctaUrl && (
                    <p className="mt-6" data-id={`feature-item-cta-wrapper-${index}`}>
                      <a
                        padi-analytics={feature.ctaDataAnalytics}
                        href={feature.ctaUrl}
                        className="text-sm font-semibold leading-5 text-padi-blue hover:text-padi-blue-darker"
                        data-id={`feature-item-cta-${index}`}
                      >
                        {feature.ctaLabel}{' '}
                        <span aria-hidden="true" data-id={`feature-item-cta-arrow-${index}`}>
                          →
                        </span>
                      </a>
                    </p>
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
};

export default FeatureSectionDynamicList;
